/**
 * Checkpoints Service
 *
 * API endpoints:
 * - GET    /checkpoints/get/{spreadId} -> Get checkpoint metadata by spread ID
 * - POST   /checkpoints/{spreadId}/exit-checkpoint -> Leave spread session; last user may purge Redis and stage recompose
 * - POST   /checkpoints/{spreadId}/reset -> Purge the spread's checkpoint, re-pull NSD, clear the user layer, recompose
 * - DELETE /checkpoints/{spreadId}/purge -> Purge the spread's checkpoint data
 */

import { httpClient } from '@/api/http/client'
import { logger } from '@/utils/logger'

export interface ResetCheckpointResponse {
  message?: string
  [key: string]: unknown
}

export interface ExitCheckpointResponse {
  message?: string
  [key: string]: unknown
}

export interface PurgeCheckpointResponse {
  message?: string
  [key: string]: unknown
}

async function getById(spreadId: string): Promise<Record<string, unknown>> {
  return httpClient.get<Record<string, unknown>>(`/checkpoints/get/${spreadId}`)
}

async function resetCheckpoint(spreadId: string): Promise<ResetCheckpointResponse> {
  logger.warn(`Resetting checkpoint ${spreadId} — clears the user layer and recomposes from NSD`)
  return httpClient.post<undefined, ResetCheckpointResponse>(
    `/checkpoints/${encodeURIComponent(spreadId)}/reset`,
    undefined
  )
}

async function exitCheckpoint(spreadId: string): Promise<ExitCheckpointResponse> {
  return httpClient.post<undefined, ExitCheckpointResponse>(
    `/checkpoints/${spreadId}/exit-checkpoint`,
    undefined
  )
}

async function purgeCheckpoint(spreadId: string): Promise<PurgeCheckpointResponse> {
  logger.warn(`Purging checkpoint data for spread ${spreadId}`)
  return httpClient.delete<PurgeCheckpointResponse>(
    `/checkpoints/${encodeURIComponent(spreadId)}/purge`
  )
}

export const checkpointsService = {
  getById,
  resetCheckpoint,
  exitCheckpoint,
  purgeCheckpoint,
}
